import {Box} from '@mui/material'
import {useEffect, useState} from 'react'
import {StyledSectionTitle, StyledTextContent, StyledTitleContainer} from '../../AdminPanelStyling'
import UseCaseDropdownPanel from './UseCaseDropdownPanel'
import PropTypes from 'prop-types'
import {getInitialUseCaseConfig} from '../Helper/UseCaseHelper'
import {formatUsecases, SECTIONS, createFilterKeyNameMap, getId} from '../../customSettings/Customization/helper'
import {cloneDeep} from 'lodash'
import {useConfig, useFilterConfig} from '../../../store/hooks/config'
import {UseCaseDomainWrapper} from './UseCaseStyles'

const processUseCases = (useCases, selectedFilterKeys) => {
  return useCases?.map((useCase) => {
    const isAllSelected = useCase.filters?.every((filter) => selectedFilterKeys?.includes(filter.filterKey))
    const updatedUseCaseObj = {...useCase, disabled: !isAllSelected}
    if (updatedUseCaseObj.disabled) {
      updatedUseCaseObj.selected = false
    }
    return updatedUseCaseObj
  })
}

const processSubTopics = (subTopics, selectedFilterKeys) => {
  return subTopics.map((subTopic) => ({
    ...subTopic,
    useCases: processUseCases(subTopic.useCases, selectedFilterKeys)
  }))
}

const processTopics = (topics, selectedFilterKeys) => {
  return topics.map((topic) => ({
    ...topic,
    subTopics: processSubTopics(topic.subTopics, selectedFilterKeys)
  }))
}

const UseCases = ({
  useCaseConfigData,
  selectedFilters,
  setUpdatedUseCases,
  showCustomLabels,
  setCustomLabelDialogProps
}) => {
  const filterConfig = useFilterConfig()
  const filterKeyNameMap = createFilterKeyNameMap(filterConfig)
  const [useCases, setUseCases] = useState(useCaseConfigData)
  const [selectedUseCases, setSelectedUseCases] = useState([])
  const [isInitialized, setIsInitialized] = useState(false)
  const {useCasesConfig: useCasesJSONConfig} = useConfig()
  const useCasesConfigData = useCasesJSONConfig?.data

  useEffect(() => {
    if (useCaseConfigData?.length > 0) {
      setUseCases(useCaseConfigData)
    } else {
      setUseCases(getInitialUseCaseConfig(useCasesConfigData, []))
    }
  }, [useCaseConfigData, useCasesConfigData])

  useEffect(() => {
    if (selectedFilters?.length > 0 || isInitialized) {
      const selectedFilterKeys = (selectedFilters || []).map((item) => item.FILTER_KEY)
      setUseCases((prevUseCases) =>
        (prevUseCases || []).map((item) => ({
          ...item,
          topics: processTopics(item.topics, selectedFilterKeys)
        }))
      )
      setIsInitialized(true)
    }
  }, [selectedFilters, isInitialized])

  useEffect(() => {
    let selectedUseCasesData = []
    for (const item of useCases || []) {
      const useCasesForDomain = []
      for (const topic of item.topics) {
        getSelectedUseCases(topic, useCasesForDomain)
      }
      if (useCasesForDomain.length > 0) {
        selectedUseCasesData = [...selectedUseCasesData, {domain: item.name, useCases: useCasesForDomain}]
      }
    }
    setSelectedUseCases(selectedUseCasesData)
    setUpdatedUseCases([...(useCases || [])])
  }, [useCases, setUpdatedUseCases])

  const getSelectedUseCases = (topic, useCases) => {
    for (const subTopic of topic.subTopics) {
      for (const useCase of subTopic.useCases) {
        if (useCase.selected) {
          useCases.push({
            id: useCase.id,
            name: useCase.name
          })
        }
      }
    }
  }

  const handleUpdateSelectedUseCases = () => {
    let selectedUseCasesData = []
    for (const item of useCases || []) {
      const domainUseCases = []
      for (const topic of item.topics) {
        getSelectedUseCases(topic, domainUseCases)
      }
      selectedUseCasesData = [...selectedUseCasesData, {domain: item.name, useCases: domainUseCases}]
    }
    setSelectedUseCases(selectedUseCasesData)
    setUpdatedUseCases([...useCases])
  }

  const getExpandedPathForId = (items, targetId, currentPath = []) => {
    if (!Array.isArray(items) || !targetId) {
      return null
    }

    for (const item of items) {
      const rowId = item?.id || getId(item?.type, item?.name)
      const nextPath = rowId ? [...currentPath, rowId] : [...currentPath]

      if (rowId === targetId) {
        return nextPath
      }

      const nestedPath = getExpandedPathForId(item?.children, targetId, nextPath)
      if (nestedPath) {
        return nestedPath
      }
    }

    return null
  }

  const handleOpenUseCaseCustomLabels = (domain, clickedUseCaseName = '') => {
    const useCaseSourceData =
      Array.isArray(useCasesJSONConfig?.data) && useCasesJSONConfig.data.length > 0 ? useCasesJSONConfig.data : useCases
    const formattedUseCases = formatUsecases(cloneDeep(useCaseSourceData), filterKeyNameMap)
    const selectedDomainIndex = (useCases || []).findIndex((item) => item.name === domain)
    const matchedDomainByName = (formattedUseCases || []).find((item) => item.name === domain)
    const matchedDomainByIndex = selectedDomainIndex >= 0 ? formattedUseCases?.[selectedDomainIndex] : null
    const firstDomainId = formattedUseCases?.[0]?.id
    const focusedDomainId =
      matchedDomainByName?.id || matchedDomainByIndex?.id || firstDomainId || getId('domains', domain)
    const domainIds = (formattedUseCases || []).map((item) => item.id).filter(Boolean)
    const hasFocusedDomainId = domainIds.includes(focusedDomainId)
    const fallbackDomainId = matchedDomainByIndex?.id || firstDomainId || focusedDomainId
    const effectiveDomainId = hasFocusedDomainId ? focusedDomainId : fallbackDomainId

    let initialExpandedRows = {[effectiveDomainId]: true}
    const matchedDomainForUseCasePath = matchedDomainByName || matchedDomainByIndex
    if (clickedUseCaseName && matchedDomainForUseCasePath) {
      const clickedUseCaseId = getId('useCases', clickedUseCaseName)
      const expandedPath = getExpandedPathForId([matchedDomainForUseCasePath], clickedUseCaseId)
      if (expandedPath?.length > 0) {
        initialExpandedRows = expandedPath.reduce((acc, id) => {
          acc[id] = true
          return acc
        }, {})
      }
    }

    setCustomLabelDialogProps({
      open: true,
      section: SECTIONS.USE_CASES,
      data: formattedUseCases,
      initialExpandedRows
    })
  }

  return (
    <Box tabIndex={0} aria-label='Use Cases Section'>
      <StyledTitleContainer>
        <StyledSectionTitle variant='h5' tabIndex={0}>
          {'What Use Cases do you want Available?'}
        </StyledSectionTitle>
      </StyledTitleContainer>
      <StyledTextContent tabIndex={0}>
        Each of these domains serve as an umbrella to a variety of priority topics and associated use cases to choose
        from. These selections are directly linked to the permissions of both Health Conditions & Social Drivers of
        Health. Once selected, the associated filters will automatically be selected and accessed by the client.
      </StyledTextContent>
      {Array.isArray(useCases) &&
        useCases.length > 0 &&
        useCases.map((item, index) => (
          <UseCaseDomainWrapper key={item.name} isFirst={index === 0}>
            <UseCaseDropdownPanel
              domain={item.name}
              topics={item.topics}
              selectedUseCases={selectedUseCases}
              handleUpdateSelectedUseCases={handleUpdateSelectedUseCases}
              showCustomLabels={showCustomLabels}
              onCustomizeLabels={handleOpenUseCaseCustomLabels}
            />
          </UseCaseDomainWrapper>
        ))}
    </Box>
  )
}

UseCases.propTypes = {
  useCaseConfigData: PropTypes.array.isRequired,
  selectedFilters: PropTypes.array.isRequired,
  setUpdatedUseCases: PropTypes.func.isRequired,
  showCustomLabels: PropTypes.bool,
  setCustomLabelDialogProps: PropTypes.func
}

export default UseCases

/* Function descriptions:
  - processUseCases: Processes use cases based on selected filters, enabling or disabling them accordingly.
  - processSubTopics: Processes subtopics and their associated use cases based on selected filters.
  - processTopics: Processes topics and their associated subtopics based on selected filters.
  - handleUseCaseManipulation: Updates the use cases based on the selected health conditions and SDOH.
  - getSelectedUseCases: Collects selected use cases from a topic and adds them to an array.
  - handleUpdateSelectedUseCases: Updates the state with the selected use cases for each domain.
*/
